import React from 'react';

import type {AfriexBusiness} from '@/types/AfriexBusiness';

import BusinessAPIKeysTable from './BusinessAPIKeysTable';
import {useBusinessAPIKeys} from './useBusinessAPIKeys';

type Props = {
  business: AfriexBusiness | null;
};

// Merchants sometimes put a token in the callback URL's query string, so only
// the origin and path are shown.
const withoutQueryString = (url: string): string => {
  try {
    const {origin, pathname, search} = new URL(url);
    return search ? `${origin}${pathname}?…` : `${origin}${pathname}`;
  } catch {
    return url.replace(/\?.*$/s, '');
  }
};

const Row: React.FC<{label: string; children: React.ReactNode}> = ({
  label,
  children,
}) => (
  <div className="py-2 px-3 flex justify-between bg-gray-100 dark:bg-gray-900 rounded gap-3">
    <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-200">
      {label}
    </p>
    <div className="flex-[0.6] text-sm font-medium w-1/2 break-all text-gray-700 dark:text-gray-100">
      {children}
    </div>
  </div>
);

const BusinessApiSettingsTab: React.FC<Props> = ({business}) => {
  const webhookCallbackUrl = business?.meta?.webhookCallbackUrl;
  const ipWhitelist = business?.meta?.ipWhitelist ?? [];
  const {keys, isLoading, hasFailed} = useBusinessAPIKeys(business?.id ?? '');

  return (
    <div className="pt-3 px-2">
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 max-w-3xl">
        API settings the merchant configures from their business dashboard.
        Read-only here.
      </p>
      <div className="space-y-2">
        <Row label="Webhook Callback URL">
          {webhookCallbackUrl ? withoutQueryString(webhookCallbackUrl) : '-'}
        </Row>
        <Row label="IP Whitelist">
          {ipWhitelist.length ? (
            <ul>
              {ipWhitelist.map((ip) => (
                <li key={ip}>{ip}</li>
              ))}
            </ul>
          ) : (
            'None (requests accepted from any IP)'
          )}
        </Row>
        <Row label="API Version">{business?.meta?.apiVersion ?? '-'}</Row>
      </div>
      <h3 className="mt-8 mb-1 font-semibold text-gray-900 dark:text-gray-300">
        API Keys
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 max-w-3xl">
        Keys are masked; the full key is never stored. Last used is accurate to
        within an hour.
      </p>
      <BusinessAPIKeysTable
        keys={keys}
        isLoading={isLoading}
        hasFailed={hasFailed}
      />
    </div>
  );
};

export default BusinessApiSettingsTab;
